import LangEnum from '../../../lang/LangEnum';
import { BusinessPolicyType } from '../../../services/modules/business-policies/businessPolicies.types';
import {
  BusinessPolicyFactType,
  BusinessPolicyGrain,
  BusinessPolicyMeasureDTO,
  BusinessPolicyMeasureSource,
  BusinessPolicyScopeDimension,
} from '../../../services/modules/business-policies/businessPolicyCondition.types';
import { StatusType } from '../../orders/order.types';
import { UserRole } from '../../users/roles';
import { BusinessPolicyDiscountType } from '../enums/business-policy-discount-type.enum';
import { BusinessPolicyNumberOperator } from '../enums/business-policy-number-operator.enum';
import { DiscountApplicationRuleEnum } from '../enums/discount-application-rule.enum';
import {
  BusinessPolicyComparativeAvailability,
  BusinessPolicyMeasureCapability,
  BusinessPolicyMeasureKey,
  BusinessPolicyMeasureSlot,
  BusinessPolicyTypeCapability,
  BusinessPolicyTypeDefaults,
  BusinessPolicyValueInput,
  IsComparativeAcceptedParams,
  IsComparativeAllowedParams,
  IsMeasureAllowedParams,
} from './businessPolicyCapabilities.types';

const ALL_SCOPE_DIMENSIONS = [
  BusinessPolicyScopeDimension.AVAILABILITY,
  BusinessPolicyScopeDimension.DELIVERY_WINDOW,
  BusinessPolicyScopeDimension.CLASSIFICATION,
  BusinessPolicyScopeDimension.PRODUCT_COLOR,
  BusinessPolicyScopeDimension.COLLECTION,
  BusinessPolicyScopeDimension.ORDER_CREATION_DATE,
];

const SCOPE_DIMENSIONS_WITHOUT_CREATION_DATE = ALL_SCOPE_DIMENSIONS.filter(
  (dimension) => dimension !== BusinessPolicyScopeDimension.ORDER_CREATION_DATE,
);

const RESTRICTION_ROLE_OPTIONS: UserRole[] = ['ADMIN_LOJA', 'COMPRADOR', 'VENDEDOR', 'ADMIN_MARCA'];

const CASHBACK_ORDER_STATUS_OPTIONS = [
  StatusType.IN_REVISION,
  StatusType.ON_APPROVAL,
  StatusType.APPROVED,
  StatusType.SENT,
];

export const MEASURE_CAPABILITIES: Record<
  BusinessPolicyMeasureKey,
  BusinessPolicyMeasureCapability
> = {
  [BusinessPolicyMeasureKey.PIECES_ORDER]: {
    fact: BusinessPolicyFactType.PIECES,
    grain: BusinessPolicyGrain.ORDER,
    source: BusinessPolicyMeasureSource.CURRENT_ORDER,
    valueInput: BusinessPolicyValueInput.UNIT,
    scopeDimensions: ALL_SCOPE_DIMENSIONS,
    allowOrderHistory: true,
    allowPriceCompositions: false,
    supportsZeroComparisonUi: true,
    supportsUnmatchedHandlingUi: false,
    labelLangEnum: LangEnum.BUSINESS_POLICY_PIECES_LABEL,
  },
  [BusinessPolicyMeasureKey.PIECES_PRODUCT_COLOR]: {
    fact: BusinessPolicyFactType.PIECES,
    grain: BusinessPolicyGrain.PRODUCT_COLOR,
    source: BusinessPolicyMeasureSource.CURRENT_ORDER,
    valueInput: BusinessPolicyValueInput.UNIT,
    scopeDimensions: ALL_SCOPE_DIMENSIONS,
    allowOrderHistory: true,
    allowPriceCompositions: false,
    supportsZeroComparisonUi: true,
    supportsUnmatchedHandlingUi: false,
    labelLangEnum: LangEnum.BUSINESS_POLICY_PIECES_BY_PRODUCT_COLOR_LABEL,
  },
  [BusinessPolicyMeasureKey.PIECES_ORDER_PRODUCT]: {
    fact: BusinessPolicyFactType.PIECES,
    grain: BusinessPolicyGrain.ORDER_PRODUCT,
    source: BusinessPolicyMeasureSource.CURRENT_ORDER,
    valueInput: BusinessPolicyValueInput.UNIT,
    scopeDimensions: ALL_SCOPE_DIMENSIONS,
    allowOrderHistory: true,
    allowPriceCompositions: false,
    supportsZeroComparisonUi: false,
    supportsUnmatchedHandlingUi: false,
    labelLangEnum: LangEnum.BUSINESS_POLICY_PIECES_BY_ORDER_PRODUCT_LABEL,
  },
  [BusinessPolicyMeasureKey.PIECES_SUGGESTED_ORDER_PRODUCT]: {
    fact: BusinessPolicyFactType.PIECES,
    grain: BusinessPolicyGrain.ORDER_PRODUCT,
    source: BusinessPolicyMeasureSource.SUGGESTED_ORDER,
    valueInput: BusinessPolicyValueInput.UNIT,
    scopeDimensions: [],
    allowOrderHistory: false,
    allowPriceCompositions: false,
    supportsZeroComparisonUi: false,
    supportsUnmatchedHandlingUi: true,
    labelLangEnum: LangEnum.BUSINESS_POLICY_PIECES_BY_SUGGESTED_ORDER_PRODUCT_LABEL,
  },
  [BusinessPolicyMeasureKey.GROSS_VALUE_SUM_ORDER]: {
    fact: BusinessPolicyFactType.GROSS_VALUE_SUM,
    grain: BusinessPolicyGrain.ORDER,
    source: BusinessPolicyMeasureSource.CURRENT_ORDER,
    valueInput: BusinessPolicyValueInput.MONEY,
    scopeDimensions: ALL_SCOPE_DIMENSIONS,
    allowOrderHistory: true,
    allowPriceCompositions: true,
    supportsZeroComparisonUi: true,
    supportsUnmatchedHandlingUi: false,
    labelLangEnum: LangEnum.GLOBAL_ITEM_GROSS_VALUE,
  },
  [BusinessPolicyMeasureKey.UNIT_PRICE_SUM_ORDER]: {
    fact: BusinessPolicyFactType.UNIT_PRICE_SUM,
    grain: BusinessPolicyGrain.ORDER,
    source: BusinessPolicyMeasureSource.CURRENT_ORDER,
    valueInput: BusinessPolicyValueInput.MONEY,
    scopeDimensions: ALL_SCOPE_DIMENSIONS,
    allowOrderHistory: true,
    allowPriceCompositions: false,
    supportsZeroComparisonUi: true,
    supportsUnmatchedHandlingUi: false,
    labelLangEnum: LangEnum.BUSINESS_POLICY_ORDER_ITEM_UNIT_PRICE_SUM_LABEL,
  },
  [BusinessPolicyMeasureKey.NET_VALUE_ORDER]: {
    fact: BusinessPolicyFactType.NET_VALUE,
    grain: BusinessPolicyGrain.ORDER,
    source: BusinessPolicyMeasureSource.CURRENT_ORDER,
    valueInput: BusinessPolicyValueInput.MONEY,
    scopeDimensions: [],
    allowOrderHistory: false,
    allowPriceCompositions: false,
    supportsZeroComparisonUi: true,
    supportsUnmatchedHandlingUi: false,
    labelLangEnum: LangEnum.BUSINESS_POLICY_ORDER_NET_VALUE_LABEL,
  },
  [BusinessPolicyMeasureKey.CREDIT_LIMIT_ORDER]: {
    fact: BusinessPolicyFactType.CREDIT_LIMIT,
    grain: BusinessPolicyGrain.ORDER,
    source: BusinessPolicyMeasureSource.CURRENT_ORDER,
    valueInput: BusinessPolicyValueInput.UNIT,
    scopeDimensions: [],
    allowOrderHistory: false,
    allowPriceCompositions: false,
    supportsZeroComparisonUi: true,
    supportsUnmatchedHandlingUi: false,
    labelLangEnum: LangEnum.GLOBAL_CREDIT_LIMIT,
  },
  [BusinessPolicyMeasureKey.AVAILABLE_BALANCE_ORDER]: {
    fact: BusinessPolicyFactType.AVAILABLE_BALANCE,
    grain: BusinessPolicyGrain.ORDER,
    source: BusinessPolicyMeasureSource.CURRENT_ORDER,
    valueInput: BusinessPolicyValueInput.UNIT,
    scopeDimensions: [],
    allowOrderHistory: false,
    allowPriceCompositions: false,
    supportsZeroComparisonUi: true,
    supportsUnmatchedHandlingUi: false,
    labelLangEnum: LangEnum.GLOBAL_AVAILABLE_BALANCE,
  },
  [BusinessPolicyMeasureKey.VOUCHER_VALUE_ORDER]: {
    fact: BusinessPolicyFactType.VOUCHER_VALUE,
    grain: BusinessPolicyGrain.ORDER,
    source: BusinessPolicyMeasureSource.CURRENT_ORDER,
    valueInput: BusinessPolicyValueInput.UNIT,
    scopeDimensions: [],
    allowOrderHistory: false,
    allowPriceCompositions: false,
    supportsZeroComparisonUi: true,
    supportsUnmatchedHandlingUi: false,
    labelLangEnum: LangEnum.BUSINESS_POLICY_ITEM_VOUCHER_VALUE_LABEL,
  },
};

const RESTRICTION_CAPABILITY: Omit<BusinessPolicyTypeCapability, 'defaults'> = {
  measures: {
    [BusinessPolicyMeasureSlot.ROOT]: [
      BusinessPolicyMeasureKey.PIECES_ORDER,
      BusinessPolicyMeasureKey.PIECES_PRODUCT_COLOR,
      BusinessPolicyMeasureKey.PIECES_ORDER_PRODUCT,
      BusinessPolicyMeasureKey.GROSS_VALUE_SUM_ORDER,
      BusinessPolicyMeasureKey.UNIT_PRICE_SUM_ORDER,
      BusinessPolicyMeasureKey.NET_VALUE_ORDER,
    ],
    [BusinessPolicyMeasureSlot.LEFT]: [
      BusinessPolicyMeasureKey.PIECES_ORDER,
      BusinessPolicyMeasureKey.GROSS_VALUE_SUM_ORDER,
      BusinessPolicyMeasureKey.UNIT_PRICE_SUM_ORDER,
      BusinessPolicyMeasureKey.NET_VALUE_ORDER,
    ],
    [BusinessPolicyMeasureSlot.RIGHT]: [
      BusinessPolicyMeasureKey.PIECES_ORDER,
      BusinessPolicyMeasureKey.GROSS_VALUE_SUM_ORDER,
      BusinessPolicyMeasureKey.UNIT_PRICE_SUM_ORDER,
      BusinessPolicyMeasureKey.NET_VALUE_ORDER,
      BusinessPolicyMeasureKey.CREDIT_LIMIT_ORDER,
      BusinessPolicyMeasureKey.AVAILABLE_BALANCE_ORDER,
    ],
  },
  comparativeAvailability: BusinessPolicyComparativeAvailability.ALWAYS,
  scopeDimensions: ALL_SCOPE_DIMENSIONS,
  allowOrderHistory: true,
  allowPriceCompositions: true,
  discountTypes: [],
  rewards: { multiple: false },
  requirements: {
    roles: true,
    paymentMethodIds: false,
    rewardValue: false,
    integrationCodeWithTotvs: false,
  },
  roleOptions: RESTRICTION_ROLE_OPTIONS,
  activateOnOrderStatusOptions: [],
  requiresVoucherTemplate: false,
};

const buildRestrictionDefaults = (
  type:
    | BusinessPolicyType.ORDER_RESTRICTION
    | BusinessPolicyType.ORDER_WARNING
    | BusinessPolicyType.ORDER_PRODUCTS_RESTRICTION,
  rootMeasureKey: BusinessPolicyMeasureKey,
): BusinessPolicyTypeDefaults => ({
  values: { type, roles: [] },
  rootMeasureKey,
  rootNumberOperator: BusinessPolicyNumberOperator.LESS_THAN_OR_EQUAL_TO,
});

export const BUSINESS_POLICY_TYPE_CAPABILITIES: Record<
  BusinessPolicyType,
  BusinessPolicyTypeCapability
> = {
  [BusinessPolicyType.ORDER_ITEM_DISCOUNT]: {
    measures: {
      [BusinessPolicyMeasureSlot.ROOT]: [
        BusinessPolicyMeasureKey.PIECES_ORDER,
        BusinessPolicyMeasureKey.PIECES_PRODUCT_COLOR,
        BusinessPolicyMeasureKey.GROSS_VALUE_SUM_ORDER,
      ],
      [BusinessPolicyMeasureSlot.LEFT]: [
        BusinessPolicyMeasureKey.PIECES_ORDER,
        BusinessPolicyMeasureKey.PIECES_PRODUCT_COLOR,
      ],
      [BusinessPolicyMeasureSlot.RIGHT]: [BusinessPolicyMeasureKey.PIECES_ORDER],
    },
    comparativeAvailability: BusinessPolicyComparativeAvailability.NEVER,
    scopeDimensions: SCOPE_DIMENSIONS_WITHOUT_CREATION_DATE,
    allowOrderHistory: false,
    allowPriceCompositions: false,
    discountTypes: [BusinessPolicyDiscountType.PERCENTAGE, BusinessPolicyDiscountType.VALUE],
    rewards: { multiple: true },
    requirements: {
      roles: false,
      paymentMethodIds: false,
      rewardValue: true,
      integrationCodeWithTotvs: false,
    },
    roleOptions: [],
    activateOnOrderStatusOptions: [],
    requiresVoucherTemplate: false,
    defaults: {
      values: {
        type: BusinessPolicyType.ORDER_ITEM_DISCOUNT,
        discountType: BusinessPolicyDiscountType.PERCENTAGE,
        isCumulative: false,
        isAutomatic: true,
        applyDiscountOnDiscountedItems: true,
        discountApplicationRule: DiscountApplicationRuleEnum.APPLY_ON_GROSS_VALUE,
      },
      rootMeasureKey: BusinessPolicyMeasureKey.PIECES_ORDER,
      rootNumberOperator: BusinessPolicyNumberOperator.BIGGER_THAN_OR_EQUAL_TO,
    },
  },
  [BusinessPolicyType.ORDER_VOUCHER]: {
    measures: {
      [BusinessPolicyMeasureSlot.ROOT]: [
        BusinessPolicyMeasureKey.PIECES_ORDER,
        BusinessPolicyMeasureKey.UNIT_PRICE_SUM_ORDER,
      ],
      [BusinessPolicyMeasureSlot.LEFT]: [BusinessPolicyMeasureKey.UNIT_PRICE_SUM_ORDER],
      [BusinessPolicyMeasureSlot.RIGHT]: [BusinessPolicyMeasureKey.VOUCHER_VALUE_ORDER],
    },
    comparativeAvailability: BusinessPolicyComparativeAvailability.VOUCHER_TEMPLATE,
    scopeDimensions: SCOPE_DIMENSIONS_WITHOUT_CREATION_DATE,
    allowOrderHistory: true,
    allowPriceCompositions: false,
    discountTypes: [BusinessPolicyDiscountType.PERCENTAGE, BusinessPolicyDiscountType.VALUE],
    rewards: { multiple: false },
    requirements: {
      roles: false,
      paymentMethodIds: false,
      rewardValue: true,
      integrationCodeWithTotvs: true,
    },
    roleOptions: RESTRICTION_ROLE_OPTIONS,
    activateOnOrderStatusOptions: [],
    requiresVoucherTemplate: false,
    defaults: {
      values: {
        type: BusinessPolicyType.ORDER_VOUCHER,
        discountType: BusinessPolicyDiscountType.VALUE,
        isCumulative: false,
        applyDiscountOnDiscountedItems: true,
        blockedRoles: [],
        usageLimit: null,
        usageLimitPerCustomer: 1,
      },
      rootMeasureKey: BusinessPolicyMeasureKey.PIECES_ORDER,
      rootNumberOperator: BusinessPolicyNumberOperator.BIGGER_THAN_OR_EQUAL_TO,
    },
  },
  [BusinessPolicyType.CASHBACK]: {
    measures: {
      [BusinessPolicyMeasureSlot.ROOT]: [
        BusinessPolicyMeasureKey.PIECES_ORDER,
        BusinessPolicyMeasureKey.UNIT_PRICE_SUM_ORDER,
      ],
      [BusinessPolicyMeasureSlot.LEFT]: [
        BusinessPolicyMeasureKey.PIECES_ORDER,
        BusinessPolicyMeasureKey.UNIT_PRICE_SUM_ORDER,
      ],
      [BusinessPolicyMeasureSlot.RIGHT]: [
        BusinessPolicyMeasureKey.PIECES_ORDER,
        BusinessPolicyMeasureKey.UNIT_PRICE_SUM_ORDER,
      ],
    },
    comparativeAvailability: BusinessPolicyComparativeAvailability.NEVER,
    scopeDimensions: SCOPE_DIMENSIONS_WITHOUT_CREATION_DATE,
    allowOrderHistory: false,
    allowPriceCompositions: false,
    discountTypes: [
      BusinessPolicyDiscountType.FIXED_VALUE_FROM_PERCENTAGE,
      BusinessPolicyDiscountType.VALUE,
    ],
    rewards: { multiple: true },
    requirements: {
      roles: false,
      paymentMethodIds: false,
      rewardValue: true,
      integrationCodeWithTotvs: false,
    },
    roleOptions: [],
    activateOnOrderStatusOptions: CASHBACK_ORDER_STATUS_OPTIONS,
    requiresVoucherTemplate: true,
    defaults: {
      values: {
        type: BusinessPolicyType.CASHBACK,
        discountType: BusinessPolicyDiscountType.FIXED_VALUE_FROM_PERCENTAGE,
        activateOnOrderStatus: StatusType.IN_REVISION,
        startsAfterHours: 0,
        expiresAfterHours: 0,
        usageLimitPerCustomer: 1,
      },
      rootMeasureKey: BusinessPolicyMeasureKey.PIECES_ORDER,
      rootNumberOperator: BusinessPolicyNumberOperator.BIGGER_THAN_OR_EQUAL_TO,
    },
  },
  [BusinessPolicyType.ORDER_RESTRICTION]: {
    ...RESTRICTION_CAPABILITY,
    defaults: buildRestrictionDefaults(
      BusinessPolicyType.ORDER_RESTRICTION,
      BusinessPolicyMeasureKey.PIECES_ORDER,
    ),
  },
  [BusinessPolicyType.ORDER_WARNING]: {
    ...RESTRICTION_CAPABILITY,
    defaults: buildRestrictionDefaults(
      BusinessPolicyType.ORDER_WARNING,
      BusinessPolicyMeasureKey.PIECES_ORDER,
    ),
  },
  [BusinessPolicyType.ORDER_PRODUCTS_RESTRICTION]: {
    ...RESTRICTION_CAPABILITY,
    measures: {
      [BusinessPolicyMeasureSlot.ROOT]: [
        BusinessPolicyMeasureKey.PIECES_ORDER_PRODUCT,
        BusinessPolicyMeasureKey.GROSS_VALUE_SUM_ORDER,
      ],
      [BusinessPolicyMeasureSlot.LEFT]: [BusinessPolicyMeasureKey.PIECES_ORDER_PRODUCT],
      [BusinessPolicyMeasureSlot.RIGHT]: [BusinessPolicyMeasureKey.PIECES_SUGGESTED_ORDER_PRODUCT],
    },
    allowOrderHistory: false,
    defaults: buildRestrictionDefaults(
      BusinessPolicyType.ORDER_PRODUCTS_RESTRICTION,
      BusinessPolicyMeasureKey.PIECES_ORDER_PRODUCT,
    ),
  },
  [BusinessPolicyType.RELEASE_PAYMENT_METHODS]: {
    measures: {
      [BusinessPolicyMeasureSlot.ROOT]: [
        BusinessPolicyMeasureKey.PIECES_ORDER,
        BusinessPolicyMeasureKey.GROSS_VALUE_SUM_ORDER,
        BusinessPolicyMeasureKey.UNIT_PRICE_SUM_ORDER,
      ],
      [BusinessPolicyMeasureSlot.LEFT]: [
        BusinessPolicyMeasureKey.PIECES_ORDER,
        BusinessPolicyMeasureKey.GROSS_VALUE_SUM_ORDER,
        BusinessPolicyMeasureKey.UNIT_PRICE_SUM_ORDER,
      ],
      [BusinessPolicyMeasureSlot.RIGHT]: [
        BusinessPolicyMeasureKey.CREDIT_LIMIT_ORDER,
        BusinessPolicyMeasureKey.AVAILABLE_BALANCE_ORDER,
      ],
    },
    comparativeAvailability: BusinessPolicyComparativeAvailability.ALWAYS,
    scopeDimensions: SCOPE_DIMENSIONS_WITHOUT_CREATION_DATE,
    allowOrderHistory: true,
    allowPriceCompositions: false,
    discountTypes: [],
    rewards: { multiple: true },
    requirements: {
      roles: false,
      paymentMethodIds: true,
      rewardValue: false,
      integrationCodeWithTotvs: false,
    },
    roleOptions: [],
    activateOnOrderStatusOptions: [],
    requiresVoucherTemplate: false,
    defaults: {
      values: { type: BusinessPolicyType.RELEASE_PAYMENT_METHODS },
      rootMeasureKey: BusinessPolicyMeasureKey.PIECES_ORDER,
      rootNumberOperator: BusinessPolicyNumberOperator.BIGGER_THAN_OR_EQUAL_TO,
    },
  },
};

export const getAllowedRootMeasures = (type: BusinessPolicyType, slot: BusinessPolicyMeasureSlot) =>
  BUSINESS_POLICY_TYPE_CAPABILITIES[type].measures[slot];

export const isMeasureAllowed = ({ type, measureKey, slot }: IsMeasureAllowedParams) =>
  getAllowedRootMeasures(type, slot).includes(measureKey);

export const getAllowedScopeDimensions = (
  type: BusinessPolicyType,
  measureKey: BusinessPolicyMeasureKey,
) => {
  const typeDimensions = BUSINESS_POLICY_TYPE_CAPABILITIES[type].scopeDimensions;

  return MEASURE_CAPABILITIES[measureKey].scopeDimensions.filter((dimension) =>
    typeDimensions.includes(dimension),
  );
};

export const isOrderHistoryAllowed = (
  type: BusinessPolicyType,
  measureKey: BusinessPolicyMeasureKey,
) =>
  BUSINESS_POLICY_TYPE_CAPABILITIES[type].allowOrderHistory &&
  MEASURE_CAPABILITIES[measureKey].allowOrderHistory;

export const isComparativeAllowed = ({
  type,
  isVoucherTemplate = false,
}: IsComparativeAllowedParams) => {
  const { comparativeAvailability } = BUSINESS_POLICY_TYPE_CAPABILITIES[type];

  if (comparativeAvailability === BusinessPolicyComparativeAvailability.NEVER) {
    return false;
  }

  if (comparativeAvailability === BusinessPolicyComparativeAvailability.ALWAYS) {
    return true;
  }

  return isVoucherTemplate;
};

export const isComparativeAccepted = ({
  type,
  discountType,
  isVoucherTemplate,
}: IsComparativeAcceptedParams) => {
  if (isComparativeAllowed({ type, isVoucherTemplate })) {
    return true;
  }

  const { comparativeAvailability } = BUSINESS_POLICY_TYPE_CAPABILITIES[type];

  return (
    comparativeAvailability === BusinessPolicyComparativeAvailability.VOUCHER_TEMPLATE &&
    discountType === BusinessPolicyDiscountType.VALUE
  );
};

export const getBusinessPolicyTypeDefaults = (type: BusinessPolicyType) =>
  structuredClone(BUSINESS_POLICY_TYPE_CAPABILITIES[type].defaults.values);

const MEASURE_KEYS = Object.values(BusinessPolicyMeasureKey);

export const findMeasureKey = (
  measure: Pick<BusinessPolicyMeasureDTO, 'fact' | 'grain' | 'source'>,
) => {
  const isSuggested = measure.source === BusinessPolicyMeasureSource.SUGGESTED_ORDER;

  return (
    MEASURE_KEYS.find((key) => {
      const capability = MEASURE_CAPABILITIES[key];
      const isSuggestedCapability =
        capability.source === BusinessPolicyMeasureSource.SUGGESTED_ORDER;

      return (
        capability.fact === measure.fact &&
        capability.grain === measure.grain &&
        isSuggestedCapability === isSuggested
      );
    }) ?? null
  );
};
